<template>
    <form @submit.prevent="submit" class="multi-leg-form">
        <div class="passenger-details group">
            <div class="row">
                <label>Passenger</label>
                <Passengers :errorMessage="v$.passengers.$errors.map((e) => e.$message.toString())[0]" />
            </div>
            <div class="row">
                <label>Vehicle</label>

                <VehicleForm />
            </div>
        </div>
        <div class="travel-details">
            <RouteAndDate
                v-for="(item, index) in v$.departures.$model"
                v-model="v$.departures.$model[index]"
                :allDepartures="v$.departures.$model"
                :key="index"
                :index="index"
            >
                <div
                    v-if="v$.departures.$model.length > 1 && index == Object.keys(v$.departures.$model).length - 1"
                    @click="removeDeparture"
                    class="travel-details__remove"
                >
                    <SvgIcon class="icon" icon="close"></SvgIcon>

                    <span class="text">Remove</span>
                </div>
            </RouteAndDate>
        </div>
        <div class="button-group">
            <div v-if="state.departures.length < 4" @click="addDeparture()" class="button-group__add">
                <SvgIcon class="icon" icon="plus"></SvgIcon>

                <span>Add route</span>
            </div>
            <div class="button-group__submit">
                <VBtn type="submit">Book now</VBtn>
            </div>
        </div>
    </form>
    <div class="error-messages" v-if="v$.$errors">
        <div v-for="(item, index) in new Set(v$.$errors.map((e) => e.$message))" :key="index" class="message">
            <span v-if="item">
                <SvgIcon class="icon" icon="error"></SvgIcon>

                {{ item }}
            </span>
        </div>
    </div>
</template>

<script setup lang="ts">
import SvgIcon from "@/components/icons/SvgIcon.vue";
import useDialog from "@/composables/useDialog";
import { dateFormat } from "@/helpers/dateHelpers";
import { isValidDepartures, isValidPassengers } from "@/helpers/quickformHelpers";
import { Direction } from "@/models/Enums";
import { DialogType } from "@/models/symbols/Dialog";
import { usePassengerStore } from "@/store/passengerStore";
import { useQuickFormStore } from "@/store/quickFormStore";
import { useVehicleStore } from "@/store/vehicleStore";
import useVuelidate from "@vuelidate/core";
import { helpers } from "@vuelidate/validators";
import { AxiosError } from "axios";
import { VBtn } from "v-ferry-components";
import { computed, onMounted, reactive } from "vue";
import useCreateRequest from "../composables/useCreateRequest";
import { MultiLegDepartureDetails, MultiLegState } from "../models/MultiLegForm";
import { DepartureRequest, DepartureResponse } from "../models/QuickformApi";
import { PassengerItem } from "../models/QuickformStates";
import Passengers from "./passengers/PassengersForm.vue";
import RouteAndDate from "./RouteAndDate.vue";
import VehicleForm from "./vehicle/VehicleForm.vue";

const passengerStore = usePassengerStore();

const quickFormStore = useQuickFormStore();

const state = reactive<MultiLegState>({
    passengers: passengerStore.getPassengers(Direction.Outward) as PassengerItem[],
    departures: [] as MultiLegDepartureDetails[],
});

const rules = {
    passengers: {
        required: helpers.withMessage("Need to have at least one adult on each journey", () => {
            return isValidPassengers();
        }),
    },
    departures: {
        required: helpers.withMessage("Your dates have to be in order", () => {
            return isValidDepartures(state.departures);
        }),
        v: () => {
            return state.departures.every((departure) => {
                return departure.date && departure.route;
            });
        },
    },
};

const v$ = useVuelidate(rules, state);

const vehicleStore = useVehicleStore();

const departures = computed(() => quickFormStore.getDepartures);

onMounted(() => {
    if (departures.value.length) {
        for (let index = 0; index < departures.value.length; index++) {
            const departure = departures.value[index];
            addDeparture(departure.route, new Date(departure.date));
        }
    } else {
        addDeparture();
    }
});

const addDeparture = (route?: string, date?: Date) => {
    state.departures.push({ route: route ?? "", date: date ?? undefined });
};

const removeDeparture = () => {
    state.departures.splice(-1, 1);
};

const { create } = useCreateRequest();

const { displayDialog } = useDialog();

const submit = async () => {
    const isValid = await v$.value.$validate();

    if (!isValid) return;

    const vehiclesOutward = vehicleStore.getVehiclesOfRoute(Direction.Outward);
    const passengersOutward = passengerStore.getPassengersForApi(Direction.Outward);
    const request: DepartureRequest[] = [];

    state.departures.map((d) => {
        if (!d.date) return;
        request.push({
            date: dateFormat(d.date),
            route: d.route,
            resources: passengersOutward,
            vehicles: vehiclesOutward,
        });
    });

    create(request)
        .then((data) => {
            quickFormStore.setDepartures(request);
            window.location.href = "/book/departures";
        })
        .catch((err: AxiosError) => {
            const errResponse: DepartureResponse = err.response?.data as DepartureResponse;
            if (errResponse.error) {
                displayDialog({
                    title: errResponse.message ?? "Something went wrong",
                    message:
                        errResponse.bookitErrorCode == 12009
                            ? "Sorry - you cannot book more vehicles than the number of travellers."
                            : "Something unexpected happened, please try again",
                    confirmText: "I understand",
                    type: DialogType.danger,
                });
            } else {
                quickFormStore.setDepartures(request);
                window.location.href = "/book/departures";
            }
        });
};
</script>

<style lang="scss" scoped>
form {
    @include flex(column, space-between, unset, 25px);
    position: relative;

    .passenger-details {
        display: flex;
        flex-flow: row wrap;
        gap: 5px;
        max-width: 450px;

        @include desktop {
            max-width: 350px;
        }

        @include tablet {
            max-width: none;
        }

        .row {
            flex: 1;
            position: relative;
            label {
                font-size: 0.75rem;
                font-weight: bold;
                text-transform: uppercase;
            }
        }
    }

    .travel-details {
        @include flex(column, flex-start, unset, 30px);
        position: relative;

        &__remove {
            cursor: pointer;
            margin-top: 15px;
            span {
                color: var(--c-black);
            }
            .icon {
                color: var(--c-font-grey);
                width: 10px !important;
                height: 10px !important;

                @include tablet {
                    display: none;
                }
            }

            .text {
                display: none;

                @include tablet {
                    display: block;
                    margin: 10px 0px;
                    font-size: 0.75rem;
                    text-decoration: underline;
                }
            }
        }
    }

    :deep(.multiselect-placeholder) {
        font-size: 0.875rem;
        color: var(--c-font-grey);
        line-height: 1;
        letter-spacing: 0.3px;
        text-transform: none;
    }

    :deep(.multiselect-single-label) {
        font-size: 0.875rem;
        color: var(--c-black);
        letter-spacing: 0.3px;
        text-transform: none;
    }

    .button-group {
        @include flex(row, space-between, flex-start);

        @include tablet {
            flex-direction: column;
            align-items: center;
            gap: 20px;
        }

        &__add {
            text-transform: uppercase;
            cursor: pointer;
            font-size: 0.75em;
            font-weight: bold;
            line-height: 1;
            display: flex;
            gap: 3px;
            align-items: center;
            color: var(--c-black);

            .icon {
                width: 13px !important;
                height: 13px !important;
            }
        }

        &__submit {
            @include tablet {
                width: 100%;
            }

            @include desktop {
                max-width: 300px;
                width: 100%;
            }

            .v-btn {
                --v-btn-padding: 10px 50px;
                border-radius: 34px;
                min-width: 150px;
                padding: 12px 20px;
                font-size: 0.875rem;

                @include desktop {
                    width: 100%;
                }
            }
        }
    }
}

.error-messages {
    display: flex;
    flex-direction: column;
    gap: 10px;

    @include tablet {
        padding: 20px 0;
    }

    .icon {
        width: 15px !important;
        height: 15px !important;
    }
    span {
        display: flex;
        align-items: center;
        gap: 3px;
        font-weight: bold;
        font-size: 0.875rem;
        color: var(--c-black);
    }
}
</style>
